import React, { ChangeEvent, useMemo } from 'react'
import clsx from 'clsx'
import {
  useTable, useFlexLayout, useResizeColumns, useSortBy, HeaderGroup, EnhancedColumn, Row, Cell as TCell,
} from 'react-table'
import Cell from './Cell'
import Header from './Header'
import PlusIcon from './img/Plus'
import { Columns } from './Model'

const defaultColumn = {
  minWidth: 50,
  maxWidth: 400,
  Cell,
  Header,
  sortType: 'alphanumericFalsyLast',
}

type TableProps = {
    columns: Columns[], data: any, dispatch: ({ type }: { type: string }) => void, skipReset: boolean
    uniqueSelectSelection: boolean
    selectedCategories: string[]
    rowAddedHandler?: { (): any }
    rowRemovedHandler?: { (index: number): any }
    maximumRows?: number
    tableName?: string
    multipleFooter?: boolean
    onFooter2ValueChange?: { (name: string, value: string): any }
}

export default function Table({
  columns, data, dispatch: dataDispatch, skipReset, uniqueSelectSelection, selectedCategories,
  rowAddedHandler,
  rowRemovedHandler,
  maximumRows,
  tableName,
  multipleFooter,
  onFooter2ValueChange,
}: TableProps) {
  const sortTypes = useMemo(
    () => ({
      alphanumericFalsyLast(rowA: any, rowB: any, columnId: any, desc: any) {
        if (!rowA.values[columnId] && !rowB.values[columnId]) {
          return 0
        }

        if (!rowA.values[columnId]) {
          return desc ? -1 : 1
        }

        if (!rowB.values[columnId]) {
          return desc ? 1 : -1
        }

        return isNaN(rowA.values[columnId])
          ? rowA.values[columnId].localeCompare(rowB.values[columnId])
          : rowA.values[columnId] - rowB.values[columnId]
      },
    }),
    [],
  )

  const footer2SetValue = (e: ChangeEvent<HTMLInputElement>) => {
    const reg = /^[0-9]*(\.[0-9]+)?$/
    const { name } = e.target
    const { value } = e.target
    if (name !== null) {
      if (reg.test(value)) {
        onFooter2ValueChange(name, value)
      }
    }
  }

  const {
    getTableProps, getTableBodyProps,
    headerGroups, footerGroups,
    rows, prepareRow,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      dataDispatch,
      autoResetSortBy: !skipReset,
      autoResetFilters: !skipReset,
      autoResetRowState: !skipReset,
      sortTypes,
    },
    useFlexLayout,
    useResizeColumns,
    useSortBy,
  )

  function isTableResizing() {
    for (const headerGroup of headerGroups) {
      for (const column of headerGroup.headers) {
        if (column.isResizing) {
          return true
        }
      }
    }

    return false
  }

  function addNewRow(): void {
    dataDispatch({ type: 'add_row' })
    rowAddedHandler() // callback
  }

  const toNumber = (anything: any) => {
    const tempNumber = Number(anything)
    return Number.isNaN(tempNumber) ? 0 : tempNumber
  }

  const tableProps = tableName ? { id: tableName } : {}
  // in general, getTableProps() or getTableProps(props), if you want to supply additional props.
  return (
    <div {...getTableProps(tableProps)} className={clsx('table', isTableResizing() && 'noselect')}>
      <div>
        {headerGroups.map((headerGroup: HeaderGroup) => (
          <div {...headerGroup.getHeaderGroupProps()} className="tr">
            {headerGroup.headers.map((headers: EnhancedColumn) => <div {...headers.getHeaderProps()}>{headers.render('Header')}</div>)}
          </div>
        ))}
      </div>
      <div {...getTableBodyProps()} className="row-group">
        {rows.map((row: Row) => {
          prepareRow(row)
          return (
            <div {...row.getRowProps()} className="tr">
              {row.cells.map((cell: TCell) => (
                <div {...cell.getCellProps()} className="td">
                  {cell.render('Cell', { uniqueSelectSelection, selectedCategories, rowRemovedHandler })}
                </div>
              ))}
            </div>
          )
        })}
      </div>
      {((maximumRows === undefined) || (data.length) <= (maximumRows - 1))
        && (
        <div className="tr add-row" onClick={() => addNewRow()} tabIndex={0} onKeyDown={(e) => (e.key === 'Enter' ? addNewRow() : null)} role="grid">
          <span className="svg-icon svg-gray" style={{ marginRight: 4 }}>
            <PlusIcon />
          </span>
          Add new row
        </div>
        ) }

      <div>
        {footerGroups.map((footerGroup: HeaderGroup) => (
          <div {...footerGroup.getFooterGroupProps()} className="tr footer">
            {footerGroup.headers.map((column: EnhancedColumn) => (
              <div className="td table-footer" {...column.getFooterProps()}>{column.render('Footer')}</div>
            ))}
          </div>
        ))}
      </div>

      {multipleFooter && (
        <div>
          {footerGroups.map((footerGroup: HeaderGroup) => (
            <div {...footerGroup.getFooterGroupProps()} className="tr footer">
              {footerGroup.headers.map((column: EnhancedColumn) => (
                <div className="td table-footer" onBlur={(e: ChangeEvent<HTMLInputElement>) => footer2SetValue(e)} {...column.getFooterProps()}>{column.render('Footer2')}</div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
